import { emptySplitApi } from "./emptyApi"

/** Per-company settings at company.metadata.settings.commentModeration. */
export interface CommentModerationSettings {
  enabled: boolean
  competitors: string[]
  spamTerms: string[]
  hideNegative: boolean
  productContext?: string
  /** Hide flagged comments on Meta; off = analyse only. */
  hideComments: boolean
  slackChannelId?: string
}

export type UpdateCommentModerationSettings = Partial<CommentModerationSettings>

export type CommentPlatform = "FACEBOOK" | "INSTAGRAM"
export type CommentVerdict = "HIDE" | "KEEP"
export type CommentCategory =
  | "COMPETITOR"
  | "SPAM"
  | "NEGATIVE"
  | "QUESTION"
  | "PRAISE"
  | "NEUTRAL"
  | "OTHER"
export type CommentJudge = "RULE" | "AI" | "USER"
export type CommentStance = "POSITIVE" | "NEGATIVE" | "QUESTION" | "NEUTRAL"
export type CommentDecisionState = "hidden" | "unhidden" | "kept" | "failed"

export interface CommentDecision {
  id: string
  platform: CommentPlatform
  commentId: string
  postId: string
  pageId?: string
  adIds: string[]
  parentCommentId?: string
  authorName?: string
  message: string
  permalinkUrl?: string
  commentCreatedAt?: string
  verdict: CommentVerdict
  category: CommentCategory
  reason: string
  matchedTerm?: string
  judgedBy: CommentJudge
  confidence?: number
  stance?: CommentStance
  aspects: string[]
  hiddenAt?: string
  hideError?: string
  unhiddenAt?: string
  dryRun: boolean
  jobRunId?: string
  createdAt: string
}

export interface CommentDecisionCounts {
  total: number
  hidden: number
  kept: number
  unhidden: number
  hideFailed: number
  byCategory: Partial<Record<CommentCategory, number>>
}

export interface CommentDecisionList {
  items: CommentDecision[]
  total: number
  counts: CommentDecisionCounts
}

export interface ListCommentDecisionsArgs {
  from?: string
  to?: string
  verdict?: CommentVerdict
  state?: CommentDecisionState
  platform?: CommentPlatform
  category?: CommentCategory
  search?: string
  /** Only comments on the posts this Meta ad runs on. */
  adId?: string
  /** Only comments on this one post (organic post drawer). */
  postId?: string
  stance?: CommentStance
  take?: number
  skip?: number
}

export interface WordCloudTerm {
  term: string
  count: number
  hidden: number
  kept: number
}

export interface CommentDashboardDay {
  date: string
  competitor: number
  spam: number
  negative: number
  otherHidden: number
  kept: number
}

export interface CommentDashboardPost {
  platform: CommentPlatform
  postId: string
  permalinkUrl?: string
  adIds: string[]
  hidden: number
  kept: number
  total: number
}

export interface CommentDashboardAuthor {
  authorId?: string
  authorName: string
  hidden: number
  total: number
  categories: CommentCategory[]
}

export interface CommentDashboard {
  from?: string
  to?: string
  totals: CommentDecisionCounts
  daily: CommentDashboardDay[]
  byPlatform: Record<CommentPlatform, { hidden: number; kept: number }>
  byJudge: Partial<Record<CommentJudge, number>>
  matchedTerms: Array<{ term: string; count: number }>
  topPosts: CommentDashboardPost[]
  repeatAuthors: CommentDashboardAuthor[]
  aiHideConfidence?: number
}

export interface StanceCounts {
  positive: number
  negative: number
  question: number
  neutral: number
}

export interface AspectReaction extends StanceCounts {
  aspect: string
  total: number
}

export interface AdReaction extends StanceCounts {
  /** Meta ad id, or `organic:<postId>` for a post no ad runs on. */
  adId: string
  adName?: string
  /** True for an organic post row; `postId` then names the post. */
  organic?: boolean
  postId?: string
  platform: CommentPlatform
  postIds: string[]
  permalinkUrl?: string
  comments: number
  hidden: number
  /** (positive − negative) / judged, −1…1; null when nothing carries a stance. */
  score: number | null
  aspects: AspectReaction[]
  quotes: { positive: string[]; negative: string[]; question: string[] }
}

export interface AdReactionsReport {
  ads: AdReaction[]
  aspects: AspectReaction[]
  unscored: number
}

export interface AdCommentSummaryBody {
  themes: Array<{
    label: string
    stance: CommentStance
    mentions: number
    example?: string
  }>
  objections: Array<{ claim: string; mentions: number; example?: string }>
  questions: string[]
  suggestedReplies: Array<{ to: string; reply: string }>
}

/** Gemini-written "what the audience says" for one ad. */
export interface AdCommentSummary {
  id: string
  adId: string
  adName?: string
  platform: CommentPlatform
  postIds: string[]
  windowFrom: string
  windowTo: string
  commentCount: number
  headline: string
  overall: CommentStance
  body: AdCommentSummaryBody
  model: string
  updatedAt: string
}

/** "How will the audience react to this creative?" — from the brand's similar past creatives. */
export interface CreativeForecast {
  status: "ready" | "needs_analysis" | "no_history"
  assetId: string
  mediaType: string
  similarFound: number
  similarWithComments: number
  commentsConsidered: number
  /** Similarity-weighted expected stance shares, summing to 1. */
  expected: StanceCounts
  expectedScore: number | null
  aspects: Array<{ aspect: string; weight: number } & StanceCounts>
  objections: Array<{ claim: string; mentions: number }>
  questions: Array<{ question: string; mentions: number }>
  themes: Array<{ label: string; stance: CommentStance; mentions: number }>
  evidence: Array<{
    assetId: string
    similarity: number
    /** Weight in the expectation: similarity, scaled by the verification score once verified (0 when judged different). */
    weight: number
    mediaType: string
    analyzedAt?: string
    snippet: string
    /** Gemini's second look: does it really share the new creative's style? */
    verification?: {
      match: "same" | "similar" | "different"
      score: number
      reason: string
      sharedTraits: string[]
      differences: string[]
    }
    ads: Array<{
      adId: string
      adName?: string
      reaction?: AdReaction
      summary?: {
        headline: string
        overall: CommentStance
        commentCount: number
        body: AdCommentSummaryBody
      }
    }>
  }>
  /** Set once the similar creatives went through the Gemini verification. */
  verified?: boolean
  /** Similar creatives the verification judged different; listed, but carrying no weight. */
  rejectedSimilar?: number
  ai?: {
    headline: string
    expected: CommentStance
    confidence: "low" | "medium" | "high"
    watchOuts: string[]
    prepareReplies: Array<{ to: string; reply: string }>
  }
}

/** What the creative-forecast job leaves in JobRun.result. */
export type CreativeForecastJobResult = CreativeForecast & {
  analyzedFirst: boolean
}

export interface CreativeForecastRequest {
  assetId?: string
  adCreativeId?: string
  adId?: string
  withAi?: boolean
}

export interface PulledComment {
  platform: CommentPlatform
  commentId: string
  postId: string
  parentCommentId?: string
  authorName?: string
  message: string
  createdAt?: string
  permalinkUrl?: string
  isHidden: boolean
}

export interface CommentPullPost {
  platform: CommentPlatform
  postId: string
  pageId?: string
  adIds: string[]
  adNames: string[]
  permalinkUrl?: string
  spend: number
  /** AD: an ad runs on the post. ORGANIC: found only on the brand's own feed. */
  source?: "AD" | "ORGANIC"
  viaPageToken: boolean
  comments: PulledComment[]
  error?: string
}

export interface CommentPullResult {
  /** Non-deleted ads on the account that were scanned for posts. */
  adsScanned: number
  postsFound: number
  /** Comments older than this were not read. */
  since: string
  posts: CommentPullPost[]
}

export interface PullCommentsArgs {
  platform?: CommentPlatform
  maxPosts?: number
  lookbackDays?: number
  commentLookbackDays?: number
}

export interface RunCommentModerationArgs {
  dryRun?: boolean
  lookbackDays?: number
  commentLookbackDays?: number
}

/** Result of an `ad-comment-moderation` job run (JobRun.result). */
export interface AdCommentModerationResult {
  skipped?: "not_bound" | "not_connected"
  reason?: string
  dryRun: boolean
  lookbackDays: number
  commentSince: string
  postsFound: number
  postsScanned: number
  postsUnreachable: number
  commentsSeen: number
  commentsNew: number
  alreadyHidden: number
  hidden: number
  kept: number
  hideFailed: number
  noPageAccess: number
  aiErrors: number
  aiUnjudged: number
  byCategory: Partial<Record<CommentCategory, number>>
  unreachable: Array<{
    platform: CommentPlatform
    postId: string
    pageId?: string
    pageName?: string
    adNames: string[]
    spend: number
    reason: string
  }>
  samples: Array<{
    platform: CommentPlatform
    postId: string
    commentId: string
    author?: string
    snippet: string
    category: CommentCategory
    reason: string
    judgedBy: CommentJudge
    hideError?: string
  }>
  summaries?: Array<{
    adId: string
    adName?: string
    headline: string
    overall: CommentStance
    commentCount: number
  }>
  summaryErrors?: number
  reportUrl?: string
  slackSummary: string
}

export const commentModerationApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCommentModerationSettings: builder.query<
      CommentModerationSettings,
      void
    >({
      query: () => "comment-moderation/settings",
      providesTags: ["CommentModerationSettings"],
    }),
    updateCommentModerationSettings: builder.mutation<
      CommentModerationSettings,
      UpdateCommentModerationSettings
    >({
      query: body => ({
        url: "comment-moderation/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CommentModerationSettings"],
    }),
    listCommentDecisions: builder.query<
      CommentDecisionList,
      ListCommentDecisionsArgs
    >({
      query: params => ({ url: "comment-moderation/decisions", params }),
      providesTags: ["CommentDecisions"],
    }),
    getCommentWordCloud: builder.query<
      WordCloudTerm[],
      Pick<
        ListCommentDecisionsArgs,
        "from" | "to" | "verdict" | "platform" | "category"
      >
    >({
      query: params => ({ url: "comment-moderation/word-cloud", params }),
      providesTags: ["CommentDecisions"],
    }),
    getCommentDashboard: builder.query<
      CommentDashboard,
      Pick<ListCommentDecisionsArgs, "from" | "to" | "platform">
    >({
      query: params => ({ url: "comment-moderation/dashboard", params }),
      providesTags: ["CommentDecisions"],
    }),
    getAdReactions: builder.query<
      AdReactionsReport,
      Pick<ListCommentDecisionsArgs, "from" | "to" | "platform">
    >({
      query: params => ({ url: "comment-moderation/ad-reactions", params }),
      providesTags: ["CommentDecisions"],
    }),
    unhideComment: builder.mutation<CommentDecision, string>({
      query: id => ({
        url: `comment-moderation/decisions/${id}/unhide`,
        method: "POST",
      }),
      invalidatesTags: ["CommentDecisions"],
    }),
    hideComment: builder.mutation<CommentDecision, string>({
      query: id => ({
        url: `comment-moderation/decisions/${id}/hide`,
        method: "POST",
      }),
      invalidatesTags: ["CommentDecisions"],
    }),
    listAdSummaries: builder.query<AdCommentSummary[], void>({
      query: () => "comment-moderation/ad-summaries",
      providesTags: ["CommentSummaries"],
    }),
    refreshAdSummary: builder.mutation<
      AdCommentSummary,
      { adId: string; from?: string; to?: string }
    >({
      query: ({ adId, ...body }) => ({
        url: `comment-moderation/ad-summaries/${adId}/refresh`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CommentSummaries"],
    }),
    /** 202 + jobRunId; the job's result is a CreativeForecast (plus analyzedFirst). */
    forecastCreative: builder.mutation<
      { jobRunId: string; status: string },
      CreativeForecastRequest
    >({
      query: body => ({
        url: "comment-moderation/forecast",
        method: "POST",
        body,
      }),
    }),
    analyzeAssetForForecast: builder.mutation<
      { jobRunId: string; status: string },
      { assetIds: string[] }
    >({
      query: body => ({
        url: "comment-moderation/forecast/analyze",
        method: "POST",
        body,
      }),
    }),
    /** Read-only probe: raw comments of the first spending posts, straight from Meta. */
    pullComments: builder.query<CommentPullResult, PullCommentsArgs>({
      query: params => ({ url: "comment-moderation/pull-comments", params }),
    }),
    runCommentModeration: builder.mutation<
      { jobRunId: string; status: string },
      RunCommentModerationArgs
    >({
      query: body => ({ url: "comment-moderation/run", method: "POST", body }),
    }),
  }),
})

export const {
  useGetCommentModerationSettingsQuery,
  useUpdateCommentModerationSettingsMutation,
  useListCommentDecisionsQuery,
  useGetCommentWordCloudQuery,
  useGetCommentDashboardQuery,
  useGetAdReactionsQuery,
  useUnhideCommentMutation,
  useHideCommentMutation,
  useRunCommentModerationMutation,
  useLazyPullCommentsQuery,
  useListAdSummariesQuery,
  useRefreshAdSummaryMutation,
  useForecastCreativeMutation,
  useAnalyzeAssetForForecastMutation,
} = commentModerationApi
